import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReferenceSelection } from '@/features/agent/candidates/ai_recommendations/types/referenceSelection';
import { DEFAULT_SELECTED_SOURCES } from '@/features/agent/candidates/ai_job_search/constants';
import useCreateAiJobSearchPersona from '@/features/agent/candidates/ai_job_search/api/useCreateAiJobSearchPersona';
import useRebuildAiJobSearchPersona from '@/features/agent/candidates/ai_job_search/api/useRebuildAiJobSearchPersona';
import useFetchAiJobSearchPersona from '@/features/agent/candidates/ai_job_search/api/useFetchAiJobSearchPersona';
import useCreateAiJobSearchRun from '@/features/agent/candidates/ai_job_search/api/useCreateAiJobSearchRun';
import useFetchAiJobSearchRun from '@/features/agent/candidates/ai_job_search/api/useFetchAiJobSearchRun';
import useFetchAiJobSearchRunResults from '@/features/agent/candidates/ai_job_search/api/useFetchAiJobSearchRunResults';

/**
 * AI求人マッチングのフローステージ
 * - idle: 未開始（フォーム入力中）
 * - building: ペルソナ構築中（queued/processing ポーリング）
 * - searching: 求人検索Run 実行中（queued/processing ポーリング）
 * - completed: 検索完了（結果表示）
 * - failed: いずれかで失敗
 */
export type MatchingStage = 'idle' | 'building' | 'searching' | 'completed' | 'failed';

/** ペルソナ構築の入力 */
export type MatchingInput =
  | {
      inputType: 'candidate';
      candidateProfileId: number;
      /** カスタマイズで選んだ参照情報（custom_data / candidate_interview_log の絞り込み） */
      sourceSelection?: ReferenceSelection;
      /** カスタマイズの自由記入プロンプト */
      prompt?: string;
    }
  | { inputType: 'free_text'; freeText: string }
  | { inputType: 'uploaded_file'; signedIds: string[] };

// ReferenceSelection(customFieldIds/interviewLogIds) → SharedAiSourceSelection 形状へ変換。
// 未指定・空のときは undefined（＝全件参照）を返す。
const toSourceSelection = (selection?: ReferenceSelection) => {
  if (!selection) return undefined;
  if (selection.customFieldIds.length === 0 && selection.interviewLogIds.length === 0) {
    return undefined;
  }
  return {
    custom_data: selection.customFieldIds,
    candidate_interview_log: selection.interviewLogIds,
  };
};

// ペルソナ新規作成のリクエストを組み立てる
const toCreateRequest = (input: MatchingInput) => {
  switch (input.inputType) {
    case 'candidate':
      return {
        input_type: 'candidate',
        candidate_profile_id: input.candidateProfileId,
        selected_sources: DEFAULT_SELECTED_SOURCES,
        source_selection: toSourceSelection(input.sourceSelection),
        free_text: input.prompt || undefined,
      };
    case 'free_text':
      return { input_type: 'free_text', free_text: input.freeText };
    case 'uploaded_file':
      return { input_type: 'uploaded_file', uploaded_file_signed_ids: input.signedIds };
  }
};

// ペルソナ再構築（rebuild）のリクエストを組み立てる（input_type はペルソナ側で保持）
const toRebuildRequest = (input: MatchingInput) => {
  switch (input.inputType) {
    case 'candidate':
      return {
        selected_sources: DEFAULT_SELECTED_SOURCES,
        source_selection: toSourceSelection(input.sourceSelection),
        free_text: input.prompt || undefined,
      };
    case 'free_text':
      return { free_text: input.freeText };
    case 'uploaded_file':
      return { uploaded_file_signed_ids: input.signedIds };
  }
};

/**
 * 求人管理画面の AI求人マッチング用フロー。
 * 既存の候補者側 AI求人検索 API（ペルソナ→Run→結果）をそのまま再利用する。
 * ペルソナ復元は行わず、常にフォーム起点で構築する。
 */
export default function useAiJobMatchingFlow() {
  const [stage, setStage] = useState<MatchingStage>('idle');
  // rebuild の対象（提案開始で確定し、カスタマイズ再検索をまたいで保持する）
  const [personaId, setPersonaId] = useState<number | null>(null);
  // ポーリング対象。create/rebuild が「今回の」ペルソナを返した後にのみ立てる。
  // personaId と分離することで、再検索直後に前回 completed のキャッシュを拾って
  // 旧ペルソナで検索が走る（1回遅れ）不具合を防ぐ。
  const [pollingPersonaId, setPollingPersonaId] = useState<number | null>(null);
  const [runId, setRunId] = useState<number | null>(null);

  // ペルソナ完了→検索の自動発火を1回に抑えるガード
  const isAutoSearchingRef = useRef(false);
  // リトライ用に直近の入力を保持する
  const lastInputRef = useRef<MatchingInput | null>(null);

  const { mutateAsync: createPersona } = useCreateAiJobSearchPersona();
  const { mutateAsync: rebuildPersona } = useRebuildAiJobSearchPersona();
  const { mutateAsync: createRun } = useCreateAiJobSearchRun();

  const { data: personaData, isError: isPersonaError } = useFetchAiJobSearchPersona(
    stage === 'building' ? pollingPersonaId : null
  );
  const { data: runData, isError: isRunError } = useFetchAiJobSearchRun(
    stage === 'searching' ? runId : null
  );
  const {
    data: resultsData,
    isLoading: isResultsLoading,
    isError: isResultsError,
  } = useFetchAiJobSearchRunResults(stage === 'completed' ? runId : null);

  // 検索Runを起動する（ペルソナ完了後に呼ぶ）
  const startSearch = useCallback(
    async (targetPersonaId: number) => {
      try {
        setStage('searching');
        const run = await createRun({ job_search_persona_id: targetPersonaId });
        setRunId(run.id);
        if (run.status === 'completed') {
          setStage('completed');
        } else if (run.status === 'failed') {
          setStage('failed');
        }
        // queued/processing はそのまま searching でポーリング継続
      } catch {
        setStage('failed');
      }
    },
    [createRun]
  );

  // ペルソナ構築を開始/再構築する（提案開始・カスタマイズ再検索の共通処理）。
  // personaId が未確定なら新規作成、確定済みなら rebuild する。
  const submit = useCallback(
    async (input: MatchingInput) => {
      lastInputRef.current = input;
      isAutoSearchingRef.current = false;
      setRunId(null);
      // 前回 completed のペルソナをポーリングが拾わないよう、確定まで対象を外す
      setPollingPersonaId(null);
      setStage('building');

      try {
        let persona;
        if (personaId) {
          persona = await rebuildPersona({ id: personaId, ...toRebuildRequest(input) });
        } else {
          persona = await createPersona(toCreateRequest(input));
        }

        setPersonaId(persona.id);
        if (persona.status === 'completed') {
          isAutoSearchingRef.current = true;
          setPollingPersonaId(persona.id);
          await startSearch(persona.id);
        } else if (persona.status === 'failed') {
          setStage('failed');
        } else {
          // queued/processing のみ、今回のペルソナに対してポーリングを開始する
          setPollingPersonaId(persona.id);
        }
      } catch {
        setStage('failed');
      }
    },
    [personaId, createPersona, rebuildPersona, startSearch]
  );

  // ペルソナのポーリング結果を監視し、完了したら検索を自動起動する
  useEffect(() => {
    if (stage !== 'building' || !personaData) return;
    if (personaData.status === 'completed') {
      if (isAutoSearchingRef.current) return;
      isAutoSearchingRef.current = true;
      void startSearch(personaData.id);
    } else if (personaData.status === 'failed') {
      setStage('failed');
    }
  }, [stage, personaData, startSearch]);

  // 検索Runのポーリング結果を監視する
  useEffect(() => {
    if (stage !== 'searching' || !runData) return;
    if (runData.status === 'completed') {
      setStage('completed');
    } else if (runData.status === 'failed') {
      setStage('failed');
    }
  }, [stage, runData]);

  // 取得系エラーは failed に落とす
  useEffect(() => {
    if (isPersonaError && stage === 'building') setStage('failed');
  }, [isPersonaError, stage]);

  useEffect(() => {
    if ((isRunError || isResultsError) && (stage === 'searching' || stage === 'completed')) {
      setStage('failed');
    }
  }, [isRunError, isResultsError, stage]);

  const retry = useCallback(() => {
    if (lastInputRef.current) void submit(lastInputRef.current);
  }, [submit]);

  const reset = useCallback(() => {
    setStage('idle');
    setPersonaId(null);
    setPollingPersonaId(null);
    setRunId(null);
    isAutoSearchingRef.current = false;
    lastInputRef.current = null;
  }, []);

  const isLoading =
    stage === 'building' || stage === 'searching' || (stage === 'completed' && isResultsLoading);
  const hasResults = stage === 'completed' && !isResultsLoading && !!resultsData;

  const progressMessage =
    stage === 'building'
      ? (personaData?.progress_message ?? 'プロフィールを分析しています...')
      : stage === 'searching'
        ? (runData?.progress_message ?? '最適な求人を検索しています...')
        : null;

  const errorMessage =
    stage === 'failed'
      ? (personaData?.error_message ?? runData?.error_message ?? '処理中にエラーが発生しました')
      : null;

  return {
    stage,
    isLoading,
    hasResults,
    results: resultsData?.results ?? [],
    progressMessage,
    errorMessage,
    submit,
    retry,
    reset,
  };
}
